import { convertToValueAndUnit } from 'src/helpers/units_helpers';
import type { EnergyStatisticsResult } from './fetch_energy_statistics';
import type { RoomListEntry } from './energy_room_charts_helpers';

export type RoomDistributionResult = {
	room: RoomListEntry;
	result: EnergyStatisticsResult | null;
	error?: boolean;
};

export type RoomDistributionRow = {
	id: string;
	name: string;
	totalWh: number | null;
	valueLabel: string;
	position: number;
	error: boolean;
};

export const formatRoomDistributionWh = (wh: number): string => {
	if (wh <= 0) return '0 Wh';
	const { value, unit } = convertToValueAndUnit(wh, 'Wh');
	return `${value} ${unit}`;
};

const sumConsumptionSeries = (result: EnergyStatisticsResult): number => {
	const series =
		result.chartData?.series?.find(
			(entry) => entry.name === 'consumption' && !entry.prevPeriod && entry.data?.length,
		) ?? result.chartData?.series?.find((entry) => !entry.prevPeriod && entry.data?.length);

	return (series?.data ?? []).reduce((sum, point) => sum + Math.max(Number(point.value ?? 0), 0), 0);
};

export const getRoomDistributionTotalWh = (result: EnergyStatisticsResult | null): number | null => {
	if (!result) return null;
	const statisticTotal = Number(result.statistic?.totalConsumedValue);
	if (Number.isFinite(statisticTotal)) return Math.max(statisticTotal, 0);
	return Math.max(sumConsumptionSeries(result), 0);
};

export const buildRoomDistributionRows = (results: RoomDistributionResult[]): RoomDistributionRow[] =>
	results
		.map(({ room, result, error }) => {
			const totalWh = error ? null : getRoomDistributionTotalWh(result);
			return {
				id: String(room.id),
				name: room.name,
				totalWh,
				valueLabel: totalWh == null ? '' : formatRoomDistributionWh(totalWh),
				position: room.position ?? Number.MAX_SAFE_INTEGER,
				error: error === true,
			};
		})
		.sort((a, b) => {
			if (a.totalWh == null && b.totalWh != null) return 1;
			if (a.totalWh != null && b.totalWh == null) return -1;
			if ((b.totalWh ?? 0) !== (a.totalWh ?? 0)) return (b.totalWh ?? 0) - (a.totalWh ?? 0);
			return a.position - b.position || a.name.localeCompare(b.name);
		});

export const buildDemoRoomDistributionRows = (): RoomDistributionRow[] =>
	buildRoomDistributionRows(
		[
			['Living room', 8420],
			['Kitchen', 6180],
			['Main bedroom', 3970],
			['Office', 2440],
		].map(([name, totalWh], position) => ({
			room: { id: -(position + 1), name: String(name), position },
			result: {
				statistic: { available: true, totalConsumedValue: Number(totalWh) },
				chartData: null,
				history: [],
			} as EnergyStatisticsResult,
		})),
	);
